import AndroidBridge from "../AndroidBridge";
import IOSBridge from "../IOSBridge";
import { AppSettings } from "../store";

// Define app store URLs and deep link schemes
// Use the current origin so dev / staging / prod all open the correct
// Universal Link / App Link target. The MAUI app must declare each
// domain in its entitlements (iOS) and AndroidManifest.xml (Android)
// for OS-level interception to work.

function getAppHostUrl(): string {
  if (typeof window !== "undefined" && window.location && window.location.hostname) {
    return window.location.hostname;
  }
  return "mobile-hsnotify.hellospoke.com";
}

const APP_CONFIG = {
  ios: {
    appStoreUrl: "https://apps.apple.com/us/app/hellospoke-notify/id6740490977",
    get appScheme() {
      return `https://${getAppHostUrl()}`;
    },
    get universalLink() {
      return `https://${getAppHostUrl()}`;
    },
  },
  android: {
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.mobile.hellospoke",
    packageName: "com.mobile.hellospoke",
    get intentUrl() {
      return `intent://${getAppHostUrl()}/#Intent;scheme=https;package=com.mobile.hellospoke;end`;
    },
    appScheme: "https",
  },
};

/**
 * Detects the current platform (iOS, Android, or other)
 */
export function detectPlatform(): "ios" | "android" | "other" {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || "";
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return "ios";
  } else if (/android/i.test(userAgent)) {
    return "android";
  }
  return "other";
}

/**
 * Checks if the app is running within a native container
 */
export function isRunningInNativeApp(): boolean {
  return AndroidBridge.isAndroidAvailable() || IOSBridge.getInstance().isIOSAvailable();
}

/**
 * Opens a URL that points outside of the app (support links, plan pages, etc.)
 * in the device's default browser (Chrome/Safari) instead of navigating away
 * inside the current MAUI WebView. Always route external links through this
 * helper rather than `window.location.href`/`target="_blank"` directly, since
 * those get intercepted by the WebView and load in-place on both platforms.
 */
export function openExternalLink(url: string): void {
  if (AndroidBridge.isAndroidAvailable()) {
    AndroidBridge.openBrowser(url);
    return;
  }
  if (IOSBridge.getInstance().isIOSAvailable()) {
    IOSBridge.getInstance().openBrowser(url);
    return;
  }
  // Plain desktop/mobile browser (not embedded in the MAUI app)
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Checks if the app is running as a standalone PWA
 */
export function isRunningAsPWA(): boolean {
  // Chrome on iOS doesn't correctly report standalone mode
  const isIOSChrome = /iPad|iPhone|iPod/.test(navigator.userAgent) && /CriOS/.test(navigator.userAgent);
  const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);

  // Log the browser detection
  console.log("[AppUtils] Browser detection for PWA check:", { isIOSChrome, isIOSSafari });

  if (isIOSChrome) {
    console.log("[AppUtils] iOS Chrome detected for PWA check");
    // Return false for iOS Chrome to ensure the banner shows
    return false;
  }

  // Standard checks for Safari and other browsers
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
  const isIOSStandalone = (window.navigator as any).standalone === true;
  console.log("[AppUtils] PWA detection:", { isStandalone, isIOSStandalone });

  return isStandalone || isIOSStandalone;
}

/**
 * Gets the appropriate app store URL based on platform
 */
export function getAppStoreUrl(): string {
  const platform = detectPlatform();
  if (platform === "ios") {
    return APP_CONFIG.ios.appStoreUrl;
  } else if (platform === "android") {
    return APP_CONFIG.android.playStoreUrl;
  }
  return APP_CONFIG.android.playStoreUrl; // Default to Android
}

/**
 * Try to open the app or redirect to app store
 */
export function openApp(path: string = "", params: Record<string, string> = {}): void {
  const platform = detectPlatform();
  const appStoreUrl = getAppStoreUrl();
  const timeout = 2000; // Timeout for app open detection

  // Use provided path or default to empty
  const targetPath = path || "";
  const host = getAppHostUrl();

  // For Android
  if (platform === "android") {
    // Create the intent URL with the provided path
    const intentUrl = `intent://${host}/${targetPath}#Intent;scheme=https;package=com.mobile.hellospoke;end`;
    console.log("[AppUtils] Opening Android app with URL:", intentUrl);

    // Create a hidden iframe to attempt opening the app
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    // Try to open the app using intent URL
    window.location.href = intentUrl;

    // Fallback to Play Store after timeout if app wasn't opened
    setTimeout(() => {
      if (document.hidden || (document as any).webkitHidden) {
        // App was likely opened, do nothing
        return;
      }

      // If we're still visible after timeout, app is not installed
      window.location.href = appStoreUrl;

      // Clean up
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, timeout);

    return;
  }

  // For iOS
  if (platform === "ios") {
    // Check if we're in Chrome on iOS
    const isChrome = /CriOS/.test(navigator.userAgent);
    console.log("[AppUtils] iOS browser detection: Chrome =", isChrome);

    // Create hidden iframe for iOS to avoid page unload
    const iframe = document.createElement("iframe");
    iframe.style.border = "none";
    iframe.style.width = "1px";
    iframe.style.height = "1px";
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    // For iOS Chrome, custom schemes work better than universal links
    if (isChrome) {
      // For iOS Chrome, go directly to App Store
      // This avoids the "Cannot connect to iTunes Store" error
      console.log("[AppUtils] iOS Chrome detected, directing straight to App Store");
      window.location.href = appStoreUrl;

      // Clean up
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }

      return;
    }

    // For Safari and other iOS browsers, try universal link first
    console.log("[AppUtils] Opening iOS app with universal link");
    window.location.href = APP_CONFIG.ios.universalLink + (targetPath ? "/" + targetPath : "");

    setTimeout(() => {
      // If still here, try with custom scheme
      console.log("[AppUtils] Trying custom scheme fallback for iOS");
      window.location.href = `${APP_CONFIG.ios.appScheme}${targetPath}`;

      // Fallback to App Store if needed
      setTimeout(() => {
        if (!document.hidden) {
          console.log("[AppUtils] Fallback to App Store (iOS Safari)");
          window.location.href = appStoreUrl;
        }
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 1000);
    }, 100);

    return;
  }

  // Fallback for other platforms
  window.location.href = appStoreUrl;
}

/**
 * Check if the user has previously dismissed the app banner
 */
export function hasDismissedAppBanner(): boolean {
  // For iOS Chrome, we'll ignore previous dismissals to ensure the banner always shows
  const isIOSChrome = /iPad|iPhone|iPod/.test(navigator.userAgent) && /CriOS/.test(navigator.userAgent);
  const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);

  // Log the browser detection
  console.log("[AppUtils] Browser detection for dismissal check:", { isIOSChrome, isIOSSafari });

  if (isIOSChrome) {
    console.log("[AppUtils] iOS Chrome detected, ignoring previous banner dismissals");
    return false;
  }

  // For Safari and other browsers, check localStorage
  const dismissed = localStorage.getItem("app_banner_dismissed") === "true";
  console.log("[AppUtils] Banner dismissed status:", dismissed);
  return dismissed;
}

/**
 * Mark the app banner as dismissed
 */
export function dismissAppBanner(): void {
  localStorage.setItem("app_banner_dismissed", "true");
}

/**
 * Wait for network readiness in MAUI webview environment
 * Includes smart delays for app backgrounding scenarios
 */
export function isNativeWebView(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /;\s*wv\)/i.test(ua) || !!(window as any).Android;
}

export function hideBootSplash(): void {
  try {
    const el = document.getElementById("boot-splash");
    if (el && el.parentNode) el.parentNode.removeChild(el);
    if ((window as any).__bootSplashWatchdog) {
      clearTimeout((window as any).__bootSplashWatchdog);
      (window as any).__bootSplashWatchdog = null;
    }
  } catch {
    /* ignore */
  }
}

export async function waitForNetworkReady(): Promise<void> {
  return new Promise((resolve) => {
    // Check if we're online
    if (navigator.onLine) {
      resolve();
      return;
    }

    // Wait for online event
    const onlineHandler = () => {
      window.removeEventListener("online", onlineHandler);
      resolve();
    };

    window.addEventListener("online", onlineHandler);

    // Fallback timeout - assume ready after 5 seconds
    setTimeout(() => {
      window.removeEventListener("online", onlineHandler);
      resolve();
    }, 5000);
  });
}

/**
 * Enhanced delay strategy for MAUI webview network stability
 * Handles app backgrounding scenarios with smart delays
 */
export async function initializeNetworkForMAUI(): Promise<void> {
  // Base delay for webview initialization
  await new Promise((resolve) => setTimeout(resolve, 2000));

  // Additional delay if app was recently backgrounded
  const lastVisibilityChange = sessionStorage.getItem("lastVisibilityChange");
  if (lastVisibilityChange) {
    const timeSinceBackground = Date.now() - parseInt(lastVisibilityChange);
    // If app was backgrounded recently (less than 30 seconds ago), add extra delay
    if (timeSinceBackground < 30000) {
      await new Promise((resolve) => setTimeout(resolve, 3000)); // Extra 3 seconds
    }
  }

  // Track visibility changes for future reference
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      sessionStorage.setItem("lastVisibilityChange", Date.now().toString());
    }
  });

  // Wait for network to be ready before proceeding
  await waitForNetworkReady();
}
